import React from "react";
import * as R from "ramda";

import CandidateCard from "../../../../components/CandidateCard";
import { CANDIDATE_LISTS } from "../../../../constants/opportunity";
import {
  getCandidateOpportunityStage,
  getCandidateScreeningStatus,
  getScreeningTag,
  isCompleted,
  normalizeCandidateScreeningTags
} from "../../../../utils/candidates";
import * as gtag from "../../../../lib/gtag";

import LikeCandidateAction from "./LikeCandidateAction";

const CandidateListCard = ({
  candidate,
  history,
  list,
  opportunity,
  routeMatch,
  t,
  updateCandidateStatus,
  canMove,
  selectedCandidates = [],
  selectedCandidatesLoading = false,
  restrictAdvanceToProcessByScreening,
  isSelected,
  onSelect,
  isRecent
}) => {
  const viewed = R.pipe(
    R.pathOr([], ["views"]),
    R.includes(candidate.id)
  )(opportunity);

  const candidateScreeningStatus = getCandidateScreeningStatus(
    candidate,
    opportunity
  );
  const showAcceptedOpportunityTag =
    list === CANDIDATE_LISTS.selected && candidate.candidateLikeOpportunity;
  const candidateOpportunityStage = getCandidateOpportunityStage({
    candidate,
    selectedCandidates
  });
  const opportunityStateTag =
    list !== CANDIDATE_LISTS.selected &&
    candidateOpportunityStage === "selected"
      ? t("components:CandidateCard.alreadySelected")
      : null;

  const { screeningText = "", screeningColor, statusDataTest } = R.cond([
    [
      ({ list, candidate }) =>
        list === CANDIDATE_LISTS.selected &&
        candidate.candidateLikeOpportunity &&
        candidateScreeningStatus,
      () => getScreeningTag(t, candidateScreeningStatus)
    ],
    [
      ({ list, candidate }) =>
        list === CANDIDATE_LISTS.selected &&
        !candidate.candidateLikeOpportunity,
      () => ({
        screeningText: t("components:CandidateCard.invitationSent"),
        statusDataTest: "invitation-sent"
      })
    ],
    [
      ({ list, candidate }) =>
        candidateScreeningStatus &&
        list === CANDIDATE_LISTS.rejected &&
        candidate.isRegistered &&
        (candidate.candidateLikeOpportunity ||
          candidate.isPreviousMatch ||
          !R.propOr(true, "isRegistered", candidate)),
      () => getScreeningTag(t, candidateScreeningStatus)
    ],
    [
      ({ candidate, list }) =>
        list !== CANDIDATE_LISTS.rejected &&
        candidate.isRegistered &&
        candidateScreeningStatus,
      () => getScreeningTag(t, candidateScreeningStatus)
    ],
    [R.T, R.always({})]
  ])({ list, candidate });

  const normalizedScreeningTags = normalizeCandidateScreeningTags(candidate);
  const tags = showAcceptedOpportunityTag
    ? [
        ...normalizedScreeningTags,
        t("components:CandidateCard.acceptedOpportunity")
      ]
    : normalizedScreeningTags;
  const tagsForDisplay = opportunityStateTag
    ? [...tags, opportunityStateTag]
    : tags;

  return (
    <CandidateCard
      list={list}
      viewed={viewed}
      isRecent={isRecent}
      isSelected={isSelected}
      onSelect={onSelect}
      status={screeningText}
      highlight={!isCompleted(candidateScreeningStatus) && screeningColor}
      statusDataTest={statusDataTest}
      key={candidate.id}
      candidate={candidate}
      onSelectCandidate={() =>
        !candidate.interaction &&
        history.push(`${routeMatch.url}/${candidate.id}/info`)
      }
      onClick={() =>
        !candidate.interaction &&
        history.push(`${routeMatch.url}/${candidate.id}/info`)
      }
      actions={[
        canMove && {
          name: "like",
          customRender: (
            <LikeCandidateAction
              key={candidate.id}
              opportunity={opportunity}
              t={t}
              candidate={candidate}
              list={list}
              selectedCandidates={selectedCandidates}
              selectedCandidatesLoading={selectedCandidatesLoading}
              restrictAdvanceToProcessByScreening={
                restrictAdvanceToProcessByScreening
              }
              updateCandidateStatus={updateCandidateStatus}
            />
          )
        },
        canMove &&
          list !== CANDIDATE_LISTS.rejected && {
            name: t("components:CandidateCard.reject"),
            onClick: () => {
              gtag.event({
                category: "VacancyCandidates",
                action: `${list}.${
                  candidate.isRegistered ? "Registered" : "External"
                }.reject`,
                label: opportunity.id
              });
              updateCandidateStatus({
                candidate,
                status: "rejected",
                list,
                opportunity,
                message: t("global:good"),
                description: t("toasts:candidates.rejectedCandidate", {
                  fullname: R.or(
                    R.prop("fullname", candidate),
                    `${R.prop("first", candidate)} ${R.prop("last", candidate)}`
                  )
                })
              });
            },
            color: "error",
            iconColor: "#747c84",
            hoverColor: "#ffaa55",
            icon: "minus-circle",
            "data-test": "candidate-card-reject"
          }
      ]}
      tags={tagsForDisplay}
    />
  );
};

export default CandidateListCard;
